import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild, computed, effect, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

import { ColorCodePipe, ResizeColumnDirective, TablePaginator, TableRow } from '@shared/components/data-list/common';
import { CipoKeyboardListDirective } from '@shared/components/virtual-list';
import { DataListBase } from '@shared/components/data-list/data-list.base';
import { TableCellComponent } from '@shared/components/data-list/table-cell/table-cell.component';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@shared/consts';
import { NotFoundComponent } from '@system/not-found/not-found.component';
import { TableRowActionsComponent } from '../table-row-actions/table-row-actions.component';

@Component({
  selector: 'cipo-paged-data-list',
  templateUrl: './paged-data-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    CommonModule,
    MatMenuModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    FlexLayoutModule,
    TableCellComponent,
    ColorCodePipe,
    NotFoundComponent,
    ResizeColumnDirective,
    TableRowActionsComponent,
    CipoKeyboardListDirective,
  ],
})
export class PagedDataListComponent<T = TableRow> extends DataListBase<T> implements AfterViewInit {
  dataSource: MatTableDataSource<T> = new MatTableDataSource();
  serverPaginate = signal<TablePaginator>(null);
  selectAllDisabled = computed(() => {
    const rows = this.tableData();
    if (!rows?.length) {
      return true;
    }
    // TODO investigate why we need the cast
    return !rows.some(r => ((r as TableRow).selectable ?? true) === true);
  });
  constructor() {
    super();
    effect(() => {
      const options = this.options();
      if (options.serverPaginationSort && options.pagination) {
        this.setServerPaginatorOptions(options.pagination);
      }
    });

    effect(() => this.populateSkeleton(this.loading()));

    effect(() => {
      const rows = this.tableData();
      if (!rows) {
        this.loading.set(true);
        return;
      }
      if (!this.dataSource?.data) {
        this.dataSource = new MatTableDataSource(rows);
      } else {
        this.dataSource.data = rows;
      }
      this.checkLeftRightIcons(rows);
      this.loading.set(false);
    });

    effect(() => {
      const updatedRows = this.updatedRows();
      if (!updatedRows?.length) {
        return;
      }
      updatedRows.forEach(newRow => {
        const index = this.dataSource.data.findIndex(row => row[this.primaryKey()] === newRow[this.primaryKey()]);
        if (index !== -1) {
          this.dataSource.data[index] = newRow;
          this.dataSource.data = [...this.dataSource.data];

          /* Unselect updated rows */
          if (!this.selection()) {
            return;
          }
          const selectedRows = this.selection().selected.filter(
            row => row[this.primaryKey()] === newRow[this.primaryKey()],
          );
          if (selectedRows.length) {
            this.selection().deselect(...selectedRows);
          }
        }
      });
    });
  }

  @ViewChild('clientPaginator') paginator: MatPaginator;

  ngAfterViewInit(): void {
    const options = this.options();
    if (options.pagination) {
      if (options.serverPaginationSort) {
        this.setServerPaginatorOptions(options.pagination);
      } else {
        const { pageSize, pageSizeOptions } = options.pagination;
        this.dataSource.sort = this.sort;
        this.paginator.pageSize = pageSize;
        this.paginator.pageSizeOptions = pageSizeOptions;
        this.dataSource.paginator = this.paginator;
      }
    }
  }

  setServerPaginatorOptions({ length, pageIndex, pageSize, pageSizeOptions }: TablePaginator) {
    this.serverPaginate.set({
      length: length ?? PAGE_SIZE_OPTIONS.default[1],
      pageIndex: pageIndex ?? 0,
      pageSize: pageSize ?? PAGE_SIZE_OPTIONS.default[1],
      pageSizeOptions: pageSizeOptions ?? PAGE_SIZE_OPTIONS.default,
      pagesCount: Math.ceil((length ?? PAGE_SIZE_OPTIONS.default[1]) / (pageSize ?? PAGE_SIZE_OPTIONS.default[1])),
    });
  }

  tableSortChanged(event: Sort) {
    if (this.options().serverPaginationSort) {
      this.loading.set(true);
      this.selection()?.clear();

      // update sort options
      this.options.update(options => {
        options.sort = { ...event };
        return options;
      });

      // emit sort change event
      this.sortChanged.emit(event);
    }
  }

  paginationChange(event: TablePaginator) {
    this.loading.set(true);
    this.selection()?.clear();

    // update pagination options
    this.options.update(options => {
      options.pagination.pageIndex = event.pageIndex;
      options.pagination.pageSize = event.pageSize;
      return options;
    });

    // emit pagination change event
    this.paginationUpdated.emit(event);
  }

  isAllSelected() {
    const numSelected = this.selection().selected.length;
    const numRows = this.dataSource.data.filter(d => ((d as TableRow).selectable ?? true) !== false).length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection().clear();
      return;
    }
    // TODO investigate why we need the cast
    this.selection().select(...this.dataSource.data.filter(d => ((d as TableRow).selectable ?? true) !== false));
  }

  populateSkeleton(loading: boolean) {
    if (this.dataSource.data.length) {
      // If all empty rows are actially empty, clear the table data to show the not found state instead of skeleton
      if (this.dataSource.data.every(row => Object.keys(row).length === 1 && Object.keys(row)[0] === 'empty')) {
        this.dataSource.data = [];
      } else {
        this.dataSource.data = this.dataSource.data.map(row => ({ ...row, empty: loading }));
      }
    } else if (loading) {
      const options = this.options();
      const defaultPageSize = options.pagination?.pageSize ?? DEFAULT_PAGE_SIZE;
      const data: T[] = [];
      for (let i = 0; i < defaultPageSize; i++) {
        data.push({ empty: true } as T);
      }
      this.dataSource.data = data;
    } else {
      this.dataSource.data = [];
    }
  }
}
